<template>
    <section class="result-section">
        <div v-if="isShowDataManagingBtnRow" class="data-managing-btn-row">
            <div class="managing-btn-group left">
                <slot name="leftBtnGroup">
                    <BaseSelect
                        :optionList="VIEW_NUMBER_OPTION"
                        v-model="pagination.pageSize"
                        @update:modelValue="onPageSizeChange"
                    />

                    <h3 v-if="isShowTotalCounts" class="total-counts">
                        검색 결과：
                        <NSpin
                            v-if="isLoading"
                            :show="isLoading"
                            :style="'margin-left: 7px;'"
                        />
                        <span v-else class="font-bold"
                            >총 {{ priceFilter(pagination.itemCount) }}건</span
                        >
                    </h3>
                </slot>
            </div>

            <div class="managing-btn-group right">
                <slot name="rightBtnGroup"></slot>
                <NButton
                    v-if="isShowExcelDownloadBtn"
                    type="primary"
                    size="large"
                    ghost
                    strong
                    :style="'background: #fff;'"
                    @click="downloadExcelFile"
                    >Excel 다운로드</NButton
                >
            </div>
        </div>

        <!-- Total count slot -->
        <slot name="totalCount"></slot>

        <SkeletonBox v-if="isLoading" :height="300" style="width: 100%" />

        <NDataTable
            v-else
            remote
            :columns="columns"
            :data="data"
            :pagination="usePdsPagination ? false : pagination"
            :checkedRowKeys="checkedRowKeys"
            :scroll-x="tableWidth"
            :renderCell="renderCell"
            single-line
            @update:checkedRowKeys="onUpdateCheckedRow"
            @update:page="onPageIndexChange"
        >
            <template v-slot:empty>
                <NEmpty :description="noMessage" />
            </template>
        </NDataTable>

        <PdsPagination
            v-if="usePdsPagination && pagination && (pagination.pageCount ?? 0) > 0"
            :total="pagination.pageCount ?? 1"
            :current-page="pagination.page"
            @change="onPageIndexChange"
        />
    </section>
</template>

<script setup lang="ts">
import BaseSelect, {
    SelectValueT,
} from '@/components/common/input/BaseSelect.vue';
import SkeletonBox from '@/components/common/layout/SkeletonBox.vue';
import PdsPagination from '@/components/pds/Pagination/Pagination.vue';
import {priceFilter} from '@/filter/number-filter';
import router from '@/router';
import {isEmpty} from 'lodash-es';
import {
    DataTableBaseColumn,
    NButton,
    NDataTable,
    NEmpty,
    NSpin,
    type DataTableColumns,
} from 'naive-ui';
import {
    OnUpdateCheckedRowKeys,
    RowKey,
} from 'naive-ui/es/data-table/src/interface';
import {VNodeChild, onMounted, ref, toRefs} from 'vue';
import {useRoute} from 'vue-router';

export type RenderCellFnT = (
    value: any,
    rowData: Record<string, any>,
    column: DataTableBaseColumn,
) => VNodeChild;

// CONST
const VIEW_NUMBER_OPTION = [
    {text: '10개씩 보기', value: 10},
    {text: '30개씩 보기', value: 30},
    {text: '50개씩 보기', value: 50},
    {text: '100개씩 보기', value: 100},
];

// props, emits, libs
const props = withDefaults(
    defineProps<{
        columns: DataTableColumns<any>;
        data: any[];
        pagination?: {
            page: number; // 현재 페이지
            pageSize: number; // 한 페이지당 보이는 데이터 수
            pageCount?: number; // 총 페이지 수
            itemCount: number; // 총 데이터 수
        };
        checkedRowKeys?: RowKey[];
        isShowExcelDownloadBtn?: boolean;
        isShowTotalCounts?: boolean;
        renderCell?: RenderCellFnT;
        isLoading: boolean;
        isShowDataManagingBtnRow?: boolean;
        noMessage?: string;
        usePdsPagination?: boolean;
    }>(),
    {
        isShowExcelDownloadBtn: true,
        isShowTotalCounts: true,
        isLoading: false,
        isShowDataManagingBtnRow: true,
        noMessage: '해당 조건에 맞는 검색 결과가 존재하지 않습니다.',
        usePdsPagination: false,
    },
);

const emits = defineEmits<{
    (e: 'onPageIndexChange', currentPageIndex: number): void;
    (e: 'onPageSizeChange', pageSize: number): void;
    (e: 'downloadExcelFile'): void;
    (
        e: 'update:checkedRowKeys',
        {
            checkedKeys,
            checkedRows,
        }: {
            checkedKeys: RowKey[];
            checkedRows: any[];
        },
    ): void;
}>();

const route = useRoute();

// ref, reactive
const {pagination, checkedRowKeys} = toRefs(props);
const tableWidth = ref<number>(1000);

// computed - columns의 width를 기반으로 테이블 총 너비 계산
const calculateTableWidth = () => {
    if (props.columns && props.columns.length > 0) {
        let totalWidth = 0;
        props.columns.forEach((column: any) => {
            // width가 있으면 사용, 없으면 minWidth 사용, 둘 다 없으면 기본값 100
            const colWidth = column.width || column.minWidth || 100;
            totalWidth += colWidth;
        });
        // 최소 너비 보장 + 여유분 추가
        tableWidth.value = Math.max(totalWidth + 50, 1000);
    }
};

// methods
const onPageIndexChange = (currentPageIndex: number) => {
    router.push({
        query: {
            ...route.query,
            page: currentPageIndex,
        },
    });
    emits('onPageIndexChange', currentPageIndex);
};

const onPageSizeChange = (pageSize: SelectValueT): void => {
    router.push({
        query: {
            ...route.query,
            page: 1,
            size: pageSize,
        },
    });

    emits('onPageSizeChange', pageSize as number);
    emits('onPageIndexChange', 1);
};

const onUpdateCheckedRow: OnUpdateCheckedRowKeys = (
    checkedKeys,
    checkedRows,
) => {
    emits('update:checkedRowKeys', {checkedKeys, checkedRows});
};

const downloadExcelFile = () => {
    if (isEmpty(props.data)) {
        alert('Excel 다운로드는 데이터 조회 후 가능합니다.');
        return;
    }

    if (!confirm('조회 결과를 Excel 파일로 다운로드 받으시겠습니까?')) return;

    emits('downloadExcelFile');
};

onMounted(() => {
    // columns의 width를 기반으로 테이블 총 너비 계산
    calculateTableWidth();
});
</script>

<style lang="scss" scoped>
.spinner-wrap {
    position: relative;
    width: 100%;
    height: 300px;
    display: flex;
    justify-content: center;
    background-color: #f8f8fa;
    border-radius: 6px;
}

:deep() {
    .n-data-table {
        &-th {
            height: 50px;
        }

        &-td {
            height: 60px;

            &.editable-cell {
                .n-ellipsis {
                    display: block;
                }
            }
        }

        &__pagination {
            justify-content: center;
        }

        .cell-link {
            // cell 내부 링크 스타일
            text-decoration: underline;
            cursor: pointer;

            &:hover {
                text-decoration: none;
                opacity: 0.8;
            }
        }

        .n-empty .n-empty__icon {
            display: none;
        }
    }
}

.result-section {
    margin: 33px 0 40px;

    .data-managing-btn-row {
        margin-bottom: 13px;
        display: flex;
        justify-content: space-between;

        @media (max-width: 500px) {
            flex-direction: column;
        }

        .managing-btn-group {
            display: flex;
            align-items: center;
            gap: 0 8px;

            @media (max-width: 500px) {
                flex-direction: column;
                align-items: flex-start;

                &:last-child {
                    margin-top: 10px;
                }
            }

            &.left {
                .total-counts {
                    display: flex;
                    align-items: center;
                    margin-left: 5px;
                    font-size: 15px;
                    font-weight: 500;

                    :deep() {
                        .n-spin {
                            width: 20px;
                            height: 20px;
                        }
                    }
                }

                @media (max-width: 500px) {
                    .total-counts {
                        display: block;
                        margin-bottom: 10px;
                        order: -1;
                    }

                    :deep(.selectbox-wrap) {
                        width: 100%;
                    }
                }
            }

            &.right {
                @media (max-width: 500px) {
                    :deep(.n-button) {
                        width: 100%;
                        margin-bottom: 10px;

                        &:last-child {
                            margin-bottom: 0;
                        }
                    }
                }
            }
        }
    }
}
</style>
